import React, { useState } from 'react';
import { Mail, Phone, MapPin, RotateCcw, Loader2, ExternalLink } from 'lucide-react';

const STATUS_OPTIONS = ['pending', 'invoiced', 'paid', 'shipped', 'completed', 'refunded'];

const statusColor = {
  pending: 'bg-yellow-100 text-yellow-800',
  invoiced: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  shipped: 'bg-purple-100 text-purple-800',
  completed: 'bg-gray-200 text-gray-700',
  refunded: 'bg-red-100 text-red-700',
};

export const OrderCard = ({ order, onStatusChange, onRefund }) => {
  const [refunding, setRefunding] = useState(false);
  const [shipPrompt, setShipPrompt] = useState(false);
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [carrier, setCarrier] = useState(order.carrier || 'usps');
  const canRefund = order.payment_status === 'PAID' && order.status !== 'refunded';

  const handleStatusSelect = (status) => {
    if (status === 'shipped') {
      setShipPrompt(true);
      return;
    }
    setShipPrompt(false);
    onStatusChange(order.id, status);
  };

  const confirmShipped = () => {
    setShipPrompt(false);
    onStatusChange(order.id, 'shipped', { tracking_number: tracking.trim(), carrier });
  };

  const handleRefund = async () => {
    if (!window.confirm(`Refund $${order.total.toFixed(2)} to ${order.name}? This sends the money back to their card and can't be undone.`)) return;
    setRefunding(true);
    await onRefund(order.id);
    setRefunding(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6" data-testid={`order-card-${order.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-serif font-bold text-[#001B2A]">{order.name}</h3>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColor[order.status] || 'bg-gray-100'}`}>
              {order.status}
            </span>
            {order.payment_status === 'PAID' && order.status !== 'refunded' && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-600 text-white">💳 Square</span>
            )}
          </div>
          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
            <span className="flex items-center gap-1.5"><Mail className="h-4 w-4" />{order.email}</span>
            {order.phone && <span className="flex items-center gap-1.5"><Phone className="h-4 w-4" />{order.phone}</span>}
            <span className="flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {order.address}, {order.city} {order.state} {order.zip}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-[#001B2A]">${order.total.toFixed(2)}</p>
          <p className="text-xs text-gray-400">
            {new Date(order.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
          </p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-md p-3 mb-4">
        {order.items.map((item) => (
          <div key={item.product_id} className="flex items-center gap-3 py-1.5 text-sm">
            {item.image && <img src={item.image} alt={item.name} className="w-8 h-8 rounded object-cover" />}
            <span className="flex-1 text-gray-700">{item.name} × {item.quantity}</span>
            <span className="font-semibold">${(item.price * item.quantity).toFixed(2)}</span>
          </div>
        ))}
      </div>

      {order.notes && (
        <p className="text-sm text-gray-600 mb-4"><span className="font-semibold">Notes:</span> {order.notes}</p>
      )}

      {order.tracking_number && (
        <p className="text-sm text-gray-600 mb-4" data-testid={`order-tracking-${order.id}`}>
          <span className="font-semibold">📦 Tracking:</span> {order.tracking_number} ({(order.carrier || '').toUpperCase()})
        </p>
      )}

      {shipPrompt && (
        <div className="bg-purple-50 border border-purple-200 rounded-md p-4 mb-4" data-testid={`ship-prompt-${order.id}`}>
          <p className="text-sm font-semibold text-[#001B2A] mb-2">Add tracking info (sent to customer in the shipped email):</p>
          <div className="flex flex-wrap gap-2">
            <input
              data-testid={`tracking-input-${order.id}`}
              value={tracking}
              onChange={(e) => setTracking(e.target.value)}
              placeholder="Tracking number (optional)"
              className="flex-1 min-w-[180px] px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B]"
            />
            <select
              data-testid={`carrier-select-${order.id}`}
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B]"
            >
              <option value="usps">USPS</option>
              <option value="ups">UPS</option>
              <option value="fedex">FedEx</option>
            </select>
            <button
              data-testid={`confirm-shipped-${order.id}`}
              onClick={confirmShipped}
              className="px-4 py-1.5 bg-purple-600 text-white rounded-md text-sm font-semibold hover:bg-purple-700 transition-colors"
            >
              Mark Shipped
            </button>
            <button onClick={() => setShipPrompt(false)} className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-semibold text-gray-600">Status:</label>
        <select
          data-testid={`order-status-select-${order.id}`}
          value={order.status}
          onChange={(e) => handleStatusSelect(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm capitalize focus:outline-none focus:ring-2 focus:ring-[#7B1E2B]"
        >
          {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {order.receipt_url && (
          <a
            href={order.receipt_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-sm text-[#7B1E2B] underline"
            data-testid={`order-receipt-${order.id}`}
          >
            Receipt <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        {canRefund && (
          <button
            data-testid={`refund-button-${order.id}`}
            onClick={handleRefund}
            disabled={refunding}
            className="ml-auto flex items-center gap-2 px-4 py-1.5 border border-red-300 text-red-600 rounded-md text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-60"
          >
            {refunding ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            {refunding ? 'Refunding...' : `Refund $${order.total.toFixed(2)}`}
          </button>
        )}
      </div>
    </div>
  );
};
